/* Created by Interactive 63 - https://interactive63.com */
/**
 * Pure pointer logic for the timeline's keyframe area: hit testing, marquee selection, snapping
 * and drag clamping. Nothing here touches the DOM, so it is easy to test.
 */
import { timeToX, type TimelineView } from './ruler';
import type { KeyMark, TimelineRow } from './layout';

export const HIT_RADIUS = 7;
export const SNAP_DISTANCE_PX = 6;
export const GRID_MS = 10;

export interface Rect {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

/** The centre of a mark in keyframe-area pixels. */
export function markPosition(
  mark: KeyMark,
  rows: readonly TimelineRow[],
  view: TimelineView,
): { x: number; y: number } {
  const row = rows[mark.rowIndex];
  return { x: timeToX(mark.time, view), y: row ? row.y + row.height / 2 : 0 };
}

/** Finds the mark under a point, preferring the nearest when several overlap. */
export function hitTestMark(
  marks: readonly KeyMark[],
  rows: readonly TimelineRow[],
  view: TimelineView,
  x: number,
  y: number,
): KeyMark | undefined {
  let best: KeyMark | undefined;
  let bestDistance = HIT_RADIUS;
  for (const mark of marks) {
    const position = markPosition(mark, rows, view);
    const distance = Math.hypot(position.x - x, position.y - y);
    if (distance <= bestDistance) {
      best = mark;
      bestDistance = distance;
    }
  }
  return best;
}

/** Normalises a rectangle so x1 <= x2 and y1 <= y2. */
export function normaliseRect(rect: Rect): Rect {
  return {
    x1: Math.min(rect.x1, rect.x2),
    y1: Math.min(rect.y1, rect.y2),
    x2: Math.max(rect.x1, rect.x2),
    y2: Math.max(rect.y1, rect.y2),
  };
}

/** Returns the marks whose centres fall inside a rectangle. */
export function marqueeMarks(
  marks: readonly KeyMark[],
  rows: readonly TimelineRow[],
  view: TimelineView,
  rect: Rect,
): KeyMark[] {
  const box = normaliseRect(rect);
  return marks.filter((mark) => {
    const { x, y } = markPosition(mark, rows, view);
    return x >= box.x1 && x <= box.x2 && y >= box.y1 && y <= box.y2;
  });
}

/**
 * Snaps a time to the nearest target within six pixels. With no target in range, or snapping
 * disabled, the time is rounded to the 10 ms grid. `guide` is the target snapped to, if any.
 */
export function snapTime(
  time: number,
  targets: readonly number[],
  zoom: number,
  enabled: boolean,
): { time: number; guide: number | null } {
  if (enabled) {
    const threshold = (SNAP_DISTANCE_PX / zoom) * 1000;
    let nearest: number | null = null;
    let nearestDistance = threshold;
    for (const target of targets) {
      const distance = Math.abs(target - time);
      if (distance <= nearestDistance) {
        nearest = target;
        nearestDistance = distance;
      }
    }
    if (nearest !== null) return { time: nearest, guide: nearest };
  }
  return { time: Math.round(time / GRID_MS) * GRID_MS, guide: null };
}

/** Limits a drag delta so no dragged keyframe leaves 0..duration. */
export function clampDelta(delta: number, times: readonly number[], duration: number): number {
  if (times.length === 0) return 0;
  const earliest = Math.min(...times);
  const latest = Math.max(...times);
  return Math.min(duration - latest, Math.max(-earliest, delta));
}

export interface DragInput {
  /** Time of the keyframe that was grabbed. */
  grabbedTime: number;
  /** Time under the pointer now, minus the offset between pointer and grabbed keyframe at start. */
  pointerTime: number;
  /** Times of every keyframe being dragged. */
  draggedTimes: readonly number[];
  /** Times of keyframes that are not being dragged, plus the playhead. */
  targets: readonly number[];
  duration: number;
  zoom: number;
  snapping: boolean;
}

/** Works out a drag's total time delta, snapped and clamped, and the guide to show. */
export function computeDragDelta(input: DragInput): { delta: number; guide: number | null } {
  const targets = [0, input.duration, ...input.targets];
  const snapped = snapTime(input.pointerTime, targets, input.zoom, input.snapping);
  const delta = clampDelta(snapped.time - input.grabbedTime, input.draggedTimes, input.duration);
  const landed = input.grabbedTime + delta;
  return {
    delta,
    guide: snapped.guide !== null && landed === snapped.guide ? snapped.guide : null,
  };
}

/** Combines a clicked mark's IDs with the current selection according to the modifier keys. */
export function selectionAfterClick(
  current: readonly string[],
  markIds: readonly string[],
  modifiers: { shift: boolean; ctrl: boolean },
): string[] {
  const allSelected = markIds.every((id) => current.includes(id));
  if (modifiers.ctrl || modifiers.shift) {
    return allSelected
      ? current.filter((id) => !markIds.includes(id))
      : [...new Set([...current, ...markIds])];
  }
  return [...markIds];
}
